import { forwardRef, useCallback, useRef, useState, type ReactNode } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

/** Native select styled like the app inputs: reliable inside dialogs and friendly on phones. */
export const NativeSelect = forwardRef<HTMLSelectElement, React.ComponentProps<"select">>(function NativeSelect(
  { className, children, ...props },
  ref,
) {
  return (
    <select
      ref={ref}
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-background px-2.5 py-1 text-base md:text-sm",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-60",
        className,
      )}
      {...props}
    >
      {children}
    </select>
  );
});

export interface ConfirmOptions {
  title: string;
  text?: string;
  confirm: string;
  cancel: string;
  destructive?: boolean;
}

/** Promise-based confirmation dialog: `if (await ask({...})) …`. Render `dialog` once in the component. */
export function useConfirm(): [ReactNode, (o: ConfirmOptions) => Promise<boolean>] {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((v: boolean) => void) | null>(null);

  const ask = useCallback((o: ConfirmOptions) => {
    setOpts(o);
    return new Promise<boolean>((resolve) => { resolver.current = resolve; });
  }, []);

  const done = (v: boolean) => {
    resolver.current?.(v);
    resolver.current = null;
    setOpts(null);
  };

  const dialog = (
    <AlertDialog open={!!opts} onOpenChange={(o) => { if (!o) done(false); }}>
      <AlertDialogContent data-testid="crm-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>{opts?.title}</AlertDialogTitle>
          {opts?.text && <AlertDialogDescription>{opts.text}</AlertDialogDescription>}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => done(false)} data-testid="crm-confirm-cancel">{opts?.cancel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => done(true)}
            className={opts?.destructive ? "bg-destructive text-destructive-foreground border-destructive-border" : ""}
            data-testid="crm-confirm-ok"
          >
            {opts?.confirm}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
  return [dialog, ask];
}

/** Label + required mark + inline error around one input (Dynamics-style stacked field). */
export function FieldShell({
  label, required, error, htmlFor, children, hint, className, labelAddon,
}: {
  label: string;
  required?: boolean;
  error?: string;
  htmlFor?: string;
  children: ReactNode;
  hint?: ReactNode;
  className?: string;
  /** Small control at the end of the label row (e.g. the default e-mail star). */
  labelAddon?: ReactNode;
}) {
  return (
    <div className={cn("min-w-0 space-y-1", className)}>
      <div className="flex min-h-4 items-center gap-1">
        <label htmlFor={htmlFor} className="flex min-w-0 items-center gap-1 text-xs font-medium text-muted-foreground">
          <span className="truncate">{label}</span>
          {required && <span className="text-destructive" aria-hidden>*</span>}
        </label>
        {labelAddon && <div className="ml-auto flex shrink-0 items-center">{labelAddon}</div>}
      </div>
      {children}
      {error ? <p className="text-xs text-destructive" role="alert">{error}</p> : hint ? <div className="text-xs text-muted-foreground">{hint}</div> : null}
    </div>
  );
}

/** Read-only value box (system fields, inactive records). */
export function ReadOnlyBox({ value, testId, className }: { value: ReactNode; testId?: string; className?: string }) {
  return (
    <div
      className={cn("flex min-h-9 items-center rounded-md border border-dashed border-input bg-muted/40 px-3 py-1.5 text-sm break-words", className)}
      data-testid={testId}
    >
      {value || <span className="text-muted-foreground">—</span>}
    </div>
  );
}

/** Two-letter avatar used in the record header. */
export function Initials({ name, className }: { name: string; className?: string }) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const text = parts.length >= 2 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : (parts[0] || "?").slice(0, 2);
  return (
    <div className={cn("flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-secondary text-base font-semibold uppercase text-secondary-foreground", className)}>
      {text}
    </div>
  );
}
